/**
 * forecastSplit — how a line carries measured months and forecast months at once.
 *
 * Recharts strokes a whole <Line> one way, so a series that is solid to August and dashed
 * to December has to be TWO lines over one set of rows. This module owns the keys that
 * makes that work, so the callers never hand-build a suffixed string.
 *
 * Three keys per series, from one value list:
 *   metricId        every period — what the TOOLTIP and the y-axis domain read. Unchanged
 *                   from before this split, which is why neither had to learn about it.
 *   metricId + 'ˑm' measured periods only — the solid line.
 *   metricId + 'ˑf' forecast periods, PLUS the last measured one — the dashed line.
 *
 * The join point is the whole trick: repeating the last measured value in the forecast key
 * makes the dashed segment start ON the solid one instead of a period later, so the line
 * reads as one series changing character rather than two series with a gap.
 *
 * Why bother at all: a forecast and an actual are not the same kind of number. Drawing
 * £1,331k of September forecast with the same stroke as £479k of invoiced July revenue
 * presents one as the other, on the chart a board reads. The dash is the envelope's rule
 * (constitution §4) applied to a mark instead of a cell.
 */

const MEASURED = 'ˑm'
const FORECAST = 'ˑf'

export const measuredKey = (metricId: string): string => `${metricId}${MEASURED}`
export const forecastKey = (metricId: string): string => `${metricId}${FORECAST}`

/** True when this row's value for the series is a forecast rather than a measurement —
 *  read from the split itself, so the tooltip needs no extra key to say "(forecast)". */
export function isForecastAt(
  row: Record<string, string | number | null>,
  metricId: string
): boolean {
  return row[forecastKey(metricId)] !== null && row[measuredKey(metricId)] === null
}

/**
 * Write the three keys for one series into the already-built rows, in place.
 *
 * `values[i]` is the plotted number for period i (null = no value); `forecast[i]` says
 * whether it is an estimate. Rows are the Recharts array being assembled, one per period.
 */
export function splitSeries(
  rows: Array<Record<string, string | number | null>>,
  metricId: string,
  values: Array<number | null>,
  forecast: boolean[]
): void {
  // The last measured period, so the dashed run can start from it and meet the solid line.
  let joinAt = -1
  for (let i = 0; i < values.length; i += 1) {
    if (values[i] !== null && !forecast[i]) joinAt = i
  }
  const anyForecast = forecast.some((isEstimate, i) => isEstimate && values[i] !== null)
  for (let i = 0; i < rows.length; i += 1) {
    const value = values[i] ?? null
    rows[i][metricId] = value
    // With nothing to dash, the measured key carries the series whole — one solid line,
    // exactly as it drew before, and no empty second <Line> in the chart.
    rows[i][measuredKey(metricId)] = anyForecast && forecast[i] ? null : value
    rows[i][forecastKey(metricId)] =
      anyForecast && (forecast[i] || i === joinAt) ? value : null
  }
}
